import {Box, Chip, Stack, Tooltip, Typography} from "@mui/material";
import React, {Key, useEffect, useState} from "react";
import Grid from "@mui/material/Grid2";
import {
    Face,
    Face3,
    Face5,
    Headset,
    HelpOutline,
    HourglassBottomRounded,
    Icecream,
    Movie,
    PauseCircle,
    PlayCircle,
    QuestionMark,
    StopCircle, Tablet
} from "@mui/icons-material";
import FormField from "./FormField";
import {useTranslation} from "react-i18next";

import {Child, Gender} from "../domain/Child";
import {ProductLine} from "../domain/ProductLine";
import {RegionalAccount} from "../domain/RegionalAccount";
import {MembershipDetails, MembershipStatus} from "../domain/MembershipDetails";
import {compareDesc, format} from "date-fns";
import {DATE_TIME_CONSTANT_FORMAT} from "../util/Constants";
import BooleanField from "./BooleanField";
import {BonusEntitlement} from "../domain/BonusEntitlement";
import BonusEntitlementDisplay from "./BonusEntitlementDisplay";
import {GlobalAccountType} from "../domain/GlobalAccountType";
import DisplayNameWithGlobalAccountType from "./DisplayNameWithGlobalAccountType";

interface RegionalAccountDisplayProps {
    regionalAccount: RegionalAccount | undefined;
    membership: MembershipDetails | undefined;
    children: Array<Child> | undefined;
    products: Array<ProductLine> | undefined;
    bonusEntitlements: Array<BonusEntitlement> | undefined;
}

export default function RegionalAccountDisplay({regionalAccount, membership, children, products, bonusEntitlements} : RegionalAccountDisplayProps) {
    const globalAccountType = regionalAccount?.type ?? GlobalAccountType.UNKNOWN;
    const {t, i18n} = useTranslation();
    const [sortedEntitlements, setSortedEntitlements] = useState<Array<BonusEntitlement>>([]);


    const lookupGenderIcon = (gender: Gender) => {
        if (gender === Gender.FEMALE) {
            return <Face3/>;
        } else if (gender === Gender.MALE) {
            return <Face/>;
        } else {
            return <Face5/>;
        }
    };


    const lookupMembershipStatusIcon = (status: MembershipStatus) => {
        if (status === MembershipStatus.ACTIVE) {
            return <PlayCircle/>
        } else if (status === MembershipStatus.EXPIRED) {
            return <HourglassBottomRounded/>
        } else if (status === MembershipStatus.CANCELLED) {
            return <StopCircle/>
        } else if (status === MembershipStatus.SUSPENDED) {
            return <PauseCircle/>
        } else {
            return <HelpOutline/>
        }
    }

    const lookupProductLineCategoryIcon = (productLine: ProductLine) => {
        let icon  = <QuestionMark/>
        switch (productLine) {
            case ProductLine.PLAY_ALONG:
            case ProductLine.LETS_PLAY:
            case ProductLine.LETS_READ:
            case ProductLine.STEP_BY_STEP:
            case ProductLine.SING_ALONG:
            case ProductLine.STRAIGHT_PLAY:
                icon = < Movie/>;
                break;
            case ProductLine.SING_ALONG_AUDIO:
            case ProductLine.BEDTIME_REVIEW_AUDIO:
            case ProductLine.STORIES_AND_SONGS_AUDIO:
            case ProductLine.BEDTIME_REVIEW:
            case ProductLine.LISTEN_ALONG_AUDIO:
            case ProductLine.PLAY_ALONG_AUDIO:
            case ProductLine.SING_ALONG_SPOKEN_WORD_AUDIO:
                icon = <Headset/>
                break;
            case ProductLine.LETS_PLAY_TEASER:
            case ProductLine.STEP_BY_STEP_TEASER:
            case ProductLine.STRAIGHT_PLAY_TEASER:
                icon = <Icecream/>
                break;
            case ProductLine.ENGLISH_TAB:
                icon = <Tablet/>
                break;
            default:
                icon = <QuestionMark/>
                break;
        }
        return icon;

    }

    useEffect(() => {
        if (bonusEntitlements) {
            const clonedEntitlements = [...bonusEntitlements].sort((a:BonusEntitlement, b:BonusEntitlement) => compareDesc(a.from, b.from));

            setSortedEntitlements(clonedEntitlements);
        }

    }, [bonusEntitlements]);

    return (



        <Grid container spacing={0}>
            <Grid size={12}>
                <Box sx={{color:'primary.contrastText', bgcolor: 'primary.main', p: 1}}>
                    <Typography variant={"h6"}>{t('domain.common.regionalAccount')}</Typography>
                </Box>
            </Grid>

            <Grid size={12} sx={{bgcolor: '#ddeeff', py: 1}}>
                <Typography sx={{pl: 1}} variant={'button'}>{t('views.regionalAccountDisplay.coreAccountDetailsSection.title')}</Typography>
            </Grid>

            <Grid size={3} sx={{pb: 3}}>
                <Stack spacing={2} sx={{pl: 2}}>
                    <DisplayNameWithGlobalAccountType
                        displayName={regionalAccount?.displayName}
                        globalAccountType={globalAccountType}/>
                    <FormField title={t('domain.common.region')} value={regionalAccount?.region} />
                </Stack>
            </Grid>
            <Grid size={3}>
                <Stack spacing={2} sx={{pl: 2}}>
                    <FormField title={t('domain.common.accountNumber')} value={regionalAccount?.localUserAccount} />
                    <FormField title={t('domain.common.affinity')} value={regionalAccount?.affinity} />
                </Stack>
            </Grid>
            <Grid size={6}>
                <Stack >
                <Typography variant='overline'>{t('domain.common.children')} ({children === null ? 0 : children?.length})</Typography>
                <Stack direction='row' useFlexGap sx={{ flexWrap: 'wrap' }} spacing={{xs:0.5}}>
                {
                    children?.map(child => {
                        return (<Tooltip key={child.localChildId as Key} title={child.localChildId}>
                                <Chip key={child.localChildId as Key} icon={lookupGenderIcon(child.gender)} label={child.displayName + " - " + child.dateOfBirth} variant='outlined' sx={{p:0.5}}/>
                            </Tooltip>)
                    })
                }
                </Stack>

                <Typography variant='overline'>{t('domain.common.products')} ({products === null ? 0 : products?.length})</Typography>
                <Stack direction='row' useFlexGap sx={{ flexWrap: 'wrap' }} spacing={{xs:0.5}}>
                    {
                        products?.map(product => {
                            return (<Chip key={product as Key} icon={lookupProductLineCategoryIcon(product)} label={t(`domain.productLines.${product}`)} variant='outlined'/>)
                        })
                    }
                </Stack>
                </Stack>
            </Grid>

            <Grid size={12} sx={{bgcolor: '#ddeeff', py: 1}}>
                <Stack direction='row' spacing={{xs: 0.5}}>
                    <Typography sx={{pl: 1}} variant={'button'}>{t('domain.common.membership')}:</Typography>
                    <Typography sx={{px: 1, fontStyle: 'italic'}} variant={'button'}>{t('domain.membership.status.' + membership?.status.toString())}</Typography>
                    {lookupMembershipStatusIcon(membership?.status as MembershipStatus)}
                </Stack>
            </Grid>
            <Grid size={3}>
                <Stack spacing={2} sx={{pl: 2}}>
                    <FormField title={t('domain.membership.since')} value={membership?.since ? format(membership.since, DATE_TIME_CONSTANT_FORMAT) : '-'} />
                    <FormField title={t('domain.membership.expiry')} value={membership?.expiry ? format(membership.expiry, DATE_TIME_CONSTANT_FORMAT) : '-'}/>
                </Stack>
            </Grid>

            <Grid size={3}>
                <Stack spacing={2} sx={{pl: 2}}>
                    <Typography variant='overline'>{t('domain.membership.features')}</Typography>
                    <BooleanField value={membership?.allowEpocket} title={"E-Pocket/Facecall"} />
                    <BooleanField value={membership?.allowTelephoneEnglish} title={"Telephone English"} />
                </Stack>
            </Grid>



            <Grid size={6}>
                <Typography variant='overline'>{t('domain.common.bonusEntitlements')} ({bonusEntitlements === null ? 0 : bonusEntitlements?.length})</Typography>
                <Stack direction='row' useFlexGap sx={{flexWrap: 'wrap'}} spacing={{xs:0.5}}>
                    {
                        sortedEntitlements?.map(entitlement => {
                            return <BonusEntitlementDisplay bonusEntitlement={entitlement} />
                        })
                    }
                </Stack>
            </Grid>

        </Grid>
    );

}
